/**
 * 独立标签页里的全屏编辑器。
 *
 * 为什么要单开一个标签页：侧边栏被飞书挤在右侧一条窄栏里，三栏编辑器摊不开；
 * 竞品的编辑器就是一个独立整页。这里复用同一套 FullscreenOverlay，只是数据
 * 来源换了 —— 新标签页没有多维表格 SDK：
 *
 *   模板  ← 云同步（tpl:<accountId>）。双向：这里改完推回去，侧边栏下次打开就看到
 *   字段 + 记录数据 ← 侧边栏打开前存的快照（fsctx:<accountId>，1 小时过期）
 *
 * 打不开或快照过期时给的是可操作的提示，而不是一片白屏 —— 用户此时已经离开
 * 飞书界面，白屏等于彻底失联。
 */
import { useEffect, useMemo, useState } from 'react';
import { App as AntdApp, ConfigProvider, Spin, Result, Button } from 'antd';
import zhCN from 'antd/locale/zh_CN';
import { FullscreenOverlay } from './FullscreenOverlay';
import { getFsContext, rewriteAttachmentUrls, type FsContext } from './fscontext';
import { initCloudSync, flushPush, hasPendingPush, pullCloud } from '../store/cloudSync';
import { message } from 'antd';
import { loadContractTemplatesForTable } from '../store/persistence';
import { setLinkedMetaSnapshot } from '../contract/linkedMeta';
import { useContractTemplates } from '../contract/useContractTemplates';
import { BillingProvider } from '../billing/context';
import { ErrorBoundary } from '../components/shell/ErrorBoundary';

function Inner({ accountId, templateId }: { accountId: string; templateId: string }) {
  const [ctx, setCtx] = useState<FsContext | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [ready, setReady] = useState(false);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      // 先读上下文快照（里面带企业标识），再拉云端模板 —— 团队共享的模板按企业分区
      // KV 是最终一致的：侧边栏刚写的快照，这个标签页落到另一个边缘节点时可能还没看到。
      // 多试几次再判「过期」，别让用户白白回去重点一遍
      let c = await getFsContext(accountId);
      for (let i = 0; i < 4 && !c && !cancelled; i++) {
        await new Promise((r) => setTimeout(r, 1500));
        c = await getFsContext(accountId);
      }
      setLinkedMetaSnapshot(c?.linkedFieldMetas);
      await initCloudSync(accountId, c?.tenantKey);
      // 侧边栏刚建的模板可能还在路上：本地没有就隔一秒再拉，最多三次
      for (let i = 0; i < 3 && c && !cancelled; i++) {
        const has = loadContractTemplatesForTable(c.tableId).some((t) => t.id === templateId);
        if (has) break;
        await new Promise((r) => setTimeout(r, 1000));
        await pullCloud();
      }
      if (cancelled) return;
      if (!c) {
        setError('上下文已过期。请回到多维表格的侧边栏，重新点一次「全屏编辑」。');
      } else {
        setCtx(c);
      }
      setReady(true);
    })();
    return () => {
      cancelled = true;
    };
  }, [accountId]);

  const tpl = useContractTemplates(ctx?.tableId ?? null);
  const template = useMemo(
    () => tpl.templates.find((t) => t.id === templateId) ?? null,
    [tpl.templates, templateId],
  );

  // 关标签页前把没来得及防抖推送的改动发出去。
  // pagehide 里的推送走 keepalive，只能带 64KB —— 带印章图的模板根本发不出去，
  // 所以真正靠得住的是下面「完成并关闭」时先 await 推送成功再关；
  // 直接点浏览器的 ×，就提醒一句，让他回去点按钮。
  useEffect(() => {
    const flush = () => void flushPush();
    const warn = (e: BeforeUnloadEvent) => {
      if (!hasPendingPush()) return;
      e.preventDefault();
      e.returnValue = '';
    };
    window.addEventListener('pagehide', flush);
    window.addEventListener('beforeunload', warn);
    return () => {
      window.removeEventListener('pagehide', flush);
      window.removeEventListener('beforeunload', warn);
    };
  }, []);
  const [closing, setClosing] = useState(false);
  const saveAndClose = async () => {
    if (closing) return;
    setClosing(true);
    const hide = message.loading('正在保存到云端…', 0);
    const ok = await flushPush();
    hide();
    if (!ok) {
      setClosing(false);
      message.error('还没保存到云端（网络或配额问题），先别关，稍后再点一次「完成并关闭」', 6);
      return;
    }
    window.close();
  };

  if (!ready) {
    return (
      <div style={{ display: 'grid', placeItems: 'center', height: '100vh' }}>
        <Spin tip="正在载入模板…" size="large" />
      </div>
    );
  }
  if (error || !ctx) {
    return (
      <Result
        status="warning"
        title="没能打开全屏编辑"
        subTitle={error ?? '缺少上下文'}
        extra={
          <Button type="primary" onClick={() => window.close()}>
            关闭本页
          </Button>
        }
      />
    );
  }
  if (!template) {
    return (
      <Result
        status="warning"
        title="找不到这个模板"
        subTitle="它可能已被删除，或云同步还没跟上。回侧边栏重新打开一次即可。"
        extra={
          <Button type="primary" onClick={() => window.close()}>
            关闭本页
          </Button>
        }
      />
    );
  }

  return (
    <FullscreenOverlay
      context={{ tableId: ctx.tableId, viewId: '' }}
      // 独立页里没有 SDK：批量、发起签名这类要现取数据的动作点了必然失败，
      // 索性不显示。排版改完回侧边栏再打印。
      hideActions
      recordId={ctx.recordId}
      template={template}
      fieldMetas={ctx.fieldMetas}
      // 没有 SDK，用侧边栏存下的那份渲染数据快照
      staticData={ctx.data ? rewriteAttachmentUrls(ctx.data, accountId) : null}
      tableName={ctx.tableName}
      onUpdate={(patch) => tpl.update(template.id, patch)}
      onClose={() => void saveAndClose()}
      standalone
    />
  );
}

export function StandaloneEditor() {
  const params = new URLSearchParams(window.location.search);
  const accountId = params.get('account') || '';
  const templateId = params.get('tpl') || '';

  return (
    <ConfigProvider locale={zhCN}>
      <AntdApp>
        {/* 这一页在飞书之外单独打开，一旦渲染出错用户就彻底失联（连个返回按钮
            都没有）。错误边界必须包在最外面。 */}
        <ErrorBoundary>
          {/* 画布里的 ContractPreview 要用计费上下文（打印闸门）。
              独立页拿不到 baseUserId，Provider 会把 accountId 留空、闸门放行，
              这里不打印所以无所谓 —— 但 Provider 本身不能少，否则整棵树白屏。 */}
          <BillingProvider>
            {accountId && templateId ? (
              <Inner accountId={accountId} templateId={templateId} />
            ) : (
              <Result
                status="error"
                title="链接不完整"
                subTitle="请从多维表格侧边栏的「全屏编辑」按钮进入。"
              />
            )}
          </BillingProvider>
        </ErrorBoundary>
      </AntdApp>
    </ConfigProvider>
  );
}
